import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth0 } from '@auth0/auth0-react';

interface Character {
  _id: string;
  characterName: string;
  characterSurname: string;
  server: string;
  class: string;
  race: string;
  level: number;
  displayedGuildId?: string;
  displayedGuild?: {
    _id: string;
    name: string;
  };
  auth0Id: string;
  ownerName: string | null;
}

interface CharacterTableProps {
  characters: Character[];
  onDelete: (characterId: string) => Promise<void>;
  showActions?: boolean;
}

const CharacterTable: React.FC<CharacterTableProps> = ({ characters, onDelete, showActions = true }) => {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: keyof Character; direction: 'asc' | 'desc' }>({
    key: 'level',
    direction: 'desc'
  });

  const handleSort = (key: keyof Character) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  // Sort characters
  const sortedCharacters = [...characters].sort((a, b) => {
    if (sortConfig.key === 'displayedGuild') {
      const aGuild = a.displayedGuild?.name ?? '';
      const bGuild = b.displayedGuild?.name ?? '';
      return sortConfig.direction === 'asc'
        ? aGuild.localeCompare(bGuild)
        : bGuild.localeCompare(aGuild);
    }

    if (sortConfig.key === 'level') {
      const aLevel = a.level || 0;
      const bLevel = b.level || 0;
      return sortConfig.direction === 'asc'
        ? aLevel - bLevel
        : bLevel - aLevel;
    }

    if (sortConfig.key === 'ownerName') {
      const aName = a.ownerName || '(Hidden)';
      const bName = b.ownerName || '(Hidden)';
      return sortConfig.direction === 'asc'
        ? aName.localeCompare(bName)
        : bName.localeCompare(aName);
    }

    const aValue = String(a[sortConfig.key] || '');
    const bValue = String(b[sortConfig.key] || '');
    
    return sortConfig.direction === 'asc'
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  });

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'characterName'}
                  direction={sortConfig.direction}
                  onClick={() => handleSort('characterName')}
                >
                  Character
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'server'}
                  direction={sortConfig.direction}
                  onClick={() => handleSort('server')}
                >
                  Server
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'race'}
                  direction={sortConfig.direction}
                  onClick={() => handleSort('race')}
                >
                  Race
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'class'}
                  direction={sortConfig.direction}
                  onClick={() => handleSort('class')}
                >
                  Class
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'level'}
                  direction={sortConfig.direction}
                  onClick={() => handleSort('level')}
                >
                  Level
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'displayedGuild'}
                  direction={sortConfig.direction}
                  onClick={() => handleSort('displayedGuild')}
                >
                  Guild
                </TableSortLabel>
              </TableCell>
              {showActions && <TableCell>Actions</TableCell>}
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'ownerName'}
                  direction={sortConfig.direction}
                  onClick={() => handleSort('ownerName')}
                >
                  Real Name
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedCharacters.map((character) => (
              <TableRow key={character._id}>
                <TableCell>
                  {character.characterName}
                  {character.characterSurname && ` ${character.characterSurname}`}
                </TableCell>
                <TableCell>{character.server}</TableCell>
                <TableCell>{character.race}</TableCell>
                <TableCell>{character.class}</TableCell>
                <TableCell>{character.level}</TableCell>
                <TableCell>{character.displayedGuild?.name || ''}</TableCell>
                {showActions && (
                  <TableCell>
                    {character.auth0Id === user?.sub && (
                      <>
                        <IconButton
                          onClick={() => navigate('/edit-character', { state: { character } })}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => onDelete(character._id)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                )}
                <TableCell sx={{ color: character.ownerName ? 'inherit' : 'text.secondary' }}>
                  {character.ownerName || '(Hidden)'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default CharacterTable; 